// src/utils/dateUtils.ts
const ISO_DATE_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;

function padToTwoDigits(value: number): string {
  return value.toString().padStart(2, '0');
}

function toDateParts(
  dateValue: string
): [year: string, month: string, day: string] | null {
  if (!dateValue) {
    return null;
  }

  const candidate = dateValue.slice(0, 10);
  if (ISO_DATE_REGEX.test(candidate)) {
    const [year, month, day] = candidate.split('-');
    return [year, month, day];
  }

  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return [
    parsed.getUTCFullYear().toString(),
    padToTwoDigits(parsed.getUTCMonth() + 1),
    padToTwoDigits(parsed.getUTCDate()),
  ];
}

export function formatDateToInput(dateTimeString: string): string {
  const parts = toDateParts(dateTimeString);
  return parts ? parts.join('-') : '';
}

export function formatDateToDisplay(dateString: string): string {
  const parts = toDateParts(dateString);
  if (!parts) {
    return '';
  }

  const [year, month, day] = parts;
  return `${month}/${day}/${year}`;
}

//day-of-week compatibility with fullCalendar
export function fullCalendarDayOfWeek(dayOfWeek: number): number {
  return dayOfWeek === 7 ? 0 : dayOfWeek;
}
export function dynPricingDayOfWeek(dayOfWeek: number): number {
  return dayOfWeek === 0 ? 7 : dayOfWeek;
}

export abstract class AllDay {
  static startTime = '1900-01-01T00:00:00Z';
  static endTime = '1900-01-01T23:59:59Z';
}

/**
 * Default 1-hour range for "Add Time Window": local clock floored to the hour,
 * end = start + 1 hour. Clamped to [slotMinHour, slotMaxHour] so both boundaries
 * exist on the hourly grid (last valid window ends at slotMaxHour).
 */
export function getDefaultOneHourWindowLocal(
  slotMinTime: string,
  slotMaxTime: string
): { start: Date; end: Date } {
  const parseHour = (s: string): number => {
    const h = parseInt(s.split(':')[0] ?? '0', 10);
    return Number.isFinite(h) ? h : 0;
  };

  const minH = parseHour(slotMinTime);
  const maxH = parseHour(slotMaxTime);

  const now = new Date();
  let sh = now.getHours();
  sh = Math.min(Math.max(sh, minH), maxH - 1);

  const start = new Date(now);
  start.setHours(sh, 0, 0, 0);
  start.setMilliseconds(0);

  const end = new Date(start);
  end.setHours(sh + 1, 0, 0, 0);

  return { start, end };
}
